import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createAdminCompany,
  createAdminCompanyMaterialShape,
  createAdminCompanyMaterialType,
  createAdminCompanyUser,
  createAdminCompanyMachine,
  createAdminManualMachine,
  updateAdminCompanyUser,
  updateAdminCompanyMachine,
  resetAdminCompanyUserPassword,
  createAdminGatewayPLCConfig,
  deleteAdminGatewayPLCConfig,
  deleteAdminManualMachine,
  exportAdminGatewayPLCConfigsYAML,
  getAdminCompanies,
  getAdminCompanyMachines,
  getAdminCompanyMaterialShapes,
  getAdminCompanyMaterialTypes,
  getAdminCompanyUsers,
  getAdminGatewayConfigStatus,
  getAdminGatewayPLCConfigs,
  getAdminManualMachines,
  updateAdminCompanyMaterialShape,
  updateAdminCompanyMaterialType,
  updateAdminGatewayPLCConfig,
  updateAdminManualMachine,
} from '../api';
import {
  buildGatewayPLCConfigPayload,
  createDefaultGatewayPLCCompletionConfig,
  DEFAULT_WORK_ORDER_REGISTERS,
  SCALABLE_READ_REGISTERS,
  validateGatewayPLCConfig,
} from '../api/gatewayPLCConfig';
import {
  buildPassiveCompletionConfig,
  completionModeFromConfig,
  createPassiveCompletionConfig,
  PASSIVE_COMPLETION_DIVISORS,
} from '../api/passiveCompletionConfig';
import { machineNameFromCombinedID } from '../auth/machineIdentity';
import './AdminPage.css';

const initialCompanyForm = {
  companyID: '',
  companyName: '',
  licenseKey: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  contactMemo: '',
  adminUserID: '',
  adminPassword: '',
  adminName: '',
  adminPhone: '',
  adminEmail: '',
  adminPosition: '',
};

const initialUserForm = {
  userID: '',
  password: '',
  role: 'company_admin',
  name: '',
  contactPhone: '',
  contactEmail: '',
  position: '',
};

const initialMaterialTypeForm = { code: '', label: '', displayOrder: '' };
const initialMaterialShapeForm = { code: '', label: '', displayOrder: '' };
const initialManualMachineForm = { machineName: '', description: '' };
const defaultPassiveCompletionConfig = createPassiveCompletionConfig();
const initialPassivePLCForm = {
  combinedID: '', machineName: '', gatewayID: '', passiveProtocol: 'omron_tcp', enabled: true,
  completionMode: 'message', divisors: defaultPassiveCompletionConfig.divisors,
};
const passiveProtocolLabels = {
  omron_tcp: '오므론 Passive TCP · 8081',
  mitsubishi_tcp: '미쓰비시 Passive TCP · 8082',
};

const createRegisterRows = (registers = []) => DEFAULT_WORK_ORDER_REGISTERS.map((canonical) => {
  const saved = registers.find((register) => register.name === canonical.name && register.access === canonical.access);
  return {
    ...canonical,
    address: String(saved?.address ?? canonical.address),
    words: String(canonical.words),
    divisor: String(saved?.divisor || canonical.divisor || 1),
  };
});

const normalizeGatewayConfigStatuses = (response) => {
  if (Array.isArray(response)) return response;
  if (!response || typeof response !== 'object') return [];
  if (Array.isArray(response.statuses)) return response.statuses;
  if (Array.isArray(response.data)) return response.data;
  return [response];
};

const latestGatewayConfigStatus = (response) => normalizeGatewayConfigStatuses(response)
  .filter((status) => status && typeof status === 'object')
  .sort((left, right) => {
    const timeDifference = Date.parse(right.reportedAt || '') - Date.parse(left.reportedAt || '');
    return Number.isNaN(timeDifference) ? 0 : timeDifference;
  })[0];

const formatGatewayReportedAt = (reportedAt) => {
  const date = new Date(reportedAt || '');
  if (Number.isNaN(date.getTime())) return '';
  const kst = new Date(date.getTime() + (9 * 60 * 60 * 1000));
  const part = (value) => String(value).padStart(2, '0');
  return `${kst.getUTCFullYear()}-${part(kst.getUTCMonth() + 1)}-${part(kst.getUTCDate())} ${part(kst.getUTCHours())}:${part(kst.getUTCMinutes())}:${part(kst.getUTCSeconds())} KST`;
};

const formatManualMachineUpdatedAt = (updatedAt) => {
  const date = new Date(updatedAt || '');
  if (Number.isNaN(date.getTime())) return '-';
  const kst = new Date(date.getTime() + (9 * 60 * 60 * 1000));
  const part = (value) => String(value).padStart(2, '0');
  return `${kst.getUTCFullYear()}-${part(kst.getUTCMonth() + 1)}-${part(kst.getUTCDate())} ${part(kst.getUTCHours())}:${part(kst.getUTCMinutes())} KST`;
};

const GatewayApplyStatus = ({ config }) => {
  const reportedVersion = config.reportedConfigVersion;
  const isCurrent = config.applyStatus === 'applied'
    && Number(reportedVersion) === Number(config.configVersion);
  const isFailed = config.applyStatus === 'failed';
  const label = isFailed
    ? 'Gateway 적용 실패'
    : isCurrent
      ? 'Gateway 적용 완료'
      : config.applyStatus === 'applied'
        ? 'Gateway 적용 대기'
        : 'Gateway 보고 없음';
  const badgeClass = isCurrent ? 'true' : isFailed ? 'false' : 'pending';
  const reportedAt = formatGatewayReportedAt(config.applyReportedAt);

  return (
    <div className="gateway-apply-status">
      <span className={`status-badge ${badgeClass}`}>{label}</span>
      <div>원하는 설정 v{config.configVersion}</div>
      {reportedVersion !== undefined && reportedVersion !== null && (
        <div>Gateway 적용 v{reportedVersion}</div>
      )}
      {reportedAt && <div className="gateway-reported-at">최근 보고 {reportedAt}</div>}
      {isFailed && config.applyStatusMessage && (
        <div className="gateway-apply-error">{config.applyStatusMessage}</div>
      )}
    </div>
  );
};

const initialGatewayPLCForm = {
  combinedID: '', machineName: '', companyID: '', machineID: '', enabled: true,
  protocol: 'modbus_tcp', mode: 'active_poll', host: '', port: '502', unitID: '1',
  profileName: 'default_modbus_v1', byteOrder: 'BE', asciiByteOrder: 'BE', wordSwap: false,
  pollMs: '1000', timeoutMs: '3000', writeEnabled: false,
  completionConfig: createDefaultGatewayPLCCompletionConfig(),
  baseOffset: '0', registers: createRegisterRows(), configVersion: 0,
};

function AdminPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyID, setSelectedCompanyID] = useState('');
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [materialShapes, setMaterialShapes] = useState([]);
  const [manualMachines, setManualMachines] = useState([]);
  const [manualMachineForm, setManualMachineForm] = useState(initialManualMachineForm);
  const [editingManualMachine, setEditingManualMachine] = useState(null);
  const [showManualMachineModal, setShowManualMachineModal] = useState(false);
  const [manualMachinesLoading, setManualMachinesLoading] = useState(false);
  const [manualMachinesError, setManualMachinesError] = useState('');
  const [manualMachineMutationPending, setManualMachineMutationPending] = useState(false);
  const [companyForm, setCompanyForm] = useState(initialCompanyForm);
  const [userForm, setUserForm] = useState(initialUserForm);
  const [materialTypeForm, setMaterialTypeForm] = useState(initialMaterialTypeForm);
  const [materialShapeForm, setMaterialShapeForm] = useState(initialMaterialShapeForm);
  const [gatewayID, setGatewayID] = useState('factory-a-gw-01');
  const [gatewayPLCConfigs, setGatewayPLCConfigs] = useState([]);
  const [gatewayPLCForm, setGatewayPLCForm] = useState(initialGatewayPLCForm);
  const [editingGatewayPLCID, setEditingGatewayPLCID] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [gatewayMutationPending, setGatewayMutationPending] = useState(false);
  const gatewayListRequest = useRef(0);
  const companyDetailsRequest = useRef(0);
  const materialTypesRequest = useRef(0);
  const materialShapesRequest = useRef(0);
  const manualMachinesRequest = useRef(0);
  const manualMachineMutationRequest = useRef(0);
  const selectedCompanyIDRef = useRef(selectedCompanyID);
  const successTimerRef = useRef(null);
  const errorTimerRef = useRef(null);

  // UI 개선을 위한 탭(Tab) 및 모달(Modal) 상태
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'devices' | 'materials'
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userMutationPending, setUserMutationPending] = useState(false);
  const [showGatewayPLCModal, setShowGatewayPLCModal] = useState(false);
  const [showPassivePLCModal, setShowPassivePLCModal] = useState(false);
  const [editingPassivePLC, setEditingPassivePLC] = useState(null);
  const [passivePLCForm, setPassivePLCForm] = useState(initialPassivePLCForm);
  const [passiveMutationPending, setPassiveMutationPending] = useState(false);

  const selectedCompany = companies.find((company) => company.id === selectedCompanyID);
  selectedCompanyIDRef.current = selectedCompanyID;

  const clearToastTimers = useCallback(() => {
    if (successTimerRef.current !== null) window.clearTimeout(successTimerRef.current);
    if (errorTimerRef.current !== null) window.clearTimeout(errorTimerRef.current);
    successTimerRef.current = null;
    errorTimerRef.current = null;
  }, []);

  const showSuccess = useCallback((text) => {
    clearToastTimers();
    setMessage(text);
    setError('');
    successTimerRef.current = window.setTimeout(() => {
      successTimerRef.current = null;
      setMessage('');
    }, 4000);
  }, [clearToastTimers]);

  const showError = useCallback((err) => {
    clearToastTimers();
    setError(err?.message || String(err));
    setMessage('');
    errorTimerRef.current = window.setTimeout(() => {
      errorTimerRef.current = null;
      setError('');
    }, 5000);
  }, [clearToastTimers]);

  useEffect(() => () => clearToastTimers(), [clearToastTimers]);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminCompanies();
      setCompanies(data || []);
      if (!selectedCompanyID && data && data.length > 0) {
        setSelectedCompanyID(data[0].id);
      }
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyID, showError]);

  const loadCompanyDetails = useCallback(async (companyID) => {
    const requestID = ++companyDetailsRequest.current;
    setUsers([]);
    setMachines([]);
    if (!companyID) return;
    try {
      const [userData, machineData] = await Promise.all([
        getAdminCompanyUsers(companyID),
        getAdminCompanyMachines(companyID),
      ]);
      if (requestID !== companyDetailsRequest.current) return;
      setUsers(userData || []);
      setMachines(machineData || []);
    } catch (err) {
      if (requestID === companyDetailsRequest.current) showError(err);
    }
  }, [showError]);

  const loadGatewayPLCConfigs = useCallback(async () => {
    const requestID = ++gatewayListRequest.current;
    if (!gatewayID.trim()) {
      setGatewayPLCConfigs([]);
      return;
    }
    try {
      const [data, statusResponse] = await Promise.all([
        getAdminGatewayPLCConfigs(gatewayID.trim(), selectedCompanyID),
        getAdminGatewayConfigStatus(gatewayID.trim()),
      ]);
      if (requestID !== gatewayListRequest.current) return;
      const latestStatus = latestGatewayConfigStatus(statusResponse);
      setGatewayPLCConfigs((Array.isArray(data) ? data : []).map((config) => ({
        ...config,
        applyStatus: latestStatus?.status,
        applyStatusMessage: latestStatus?.message,
        appliedConfigVersion: latestStatus?.status === 'applied' ? latestStatus.configVersion : undefined,
        reportedConfigVersion: latestStatus?.configVersion,
        applyReportedAt: latestStatus?.reportedAt,
      })));
    } catch (err) {
      if (requestID === gatewayListRequest.current) showError(err);
    }
  }, [gatewayID, selectedCompanyID, showError]);

  const loadMaterialTypes = useCallback(async (companyID) => {
    const requestID = ++materialTypesRequest.current;
    setMaterialTypes([]);
    if (!companyID) return;
    try {
      const data = await getAdminCompanyMaterialTypes(companyID);
      if (requestID !== materialTypesRequest.current) return;
      setMaterialTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      if (requestID === materialTypesRequest.current) showError(err);
    }
  }, [showError]);

  const loadMaterialShapes = useCallback(async (companyID) => {
    const requestID = ++materialShapesRequest.current;
    setMaterialShapes([]);
    if (!companyID) return;
    try {
      const data = await getAdminCompanyMaterialShapes(companyID);
      if (requestID !== materialShapesRequest.current) return;
      setMaterialShapes(Array.isArray(data) ? data : []);
    } catch (err) {
      if (requestID === materialShapesRequest.current) showError(err);
    }
  }, [showError]);

  const loadManualMachines = useCallback(async (companyID) => {
    const requestID = ++manualMachinesRequest.current;
    setManualMachinesLoading(Boolean(companyID));
    setManualMachinesError('');
    if (!companyID) {
      setManualMachines([]);
      return false;
    }
    try {
      const data = await getAdminManualMachines(companyID);
      if (requestID !== manualMachinesRequest.current || selectedCompanyIDRef.current !== companyID) return false;
      setManualMachines(Array.isArray(data) ? data : []);
      return true;
    } catch (err) {
      if (requestID === manualMachinesRequest.current && selectedCompanyIDRef.current === companyID) {
        setManualMachinesError(err?.message || String(err));
        setManualMachines([]);
      }
      return false;
    } finally {
      if (requestID === manualMachinesRequest.current && selectedCompanyIDRef.current === companyID) {
        setManualMachinesLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadCompanyDetails(selectedCompanyID);
    setEditingUser(null);
    setShowUserModal(false);
    setShowPassivePLCModal(false);
    setEditingPassivePLC(null);
    setPassivePLCForm(initialPassivePLCForm);
  }, [selectedCompanyID, loadCompanyDetails]);

  useEffect(() => {
    if (activeTab !== 'devices') return undefined;
    loadGatewayPLCConfigs();
    const intervalID = window.setInterval(loadGatewayPLCConfigs, 5000);
    return () => window.clearInterval(intervalID);
  }, [activeTab, loadGatewayPLCConfigs]);

  useEffect(() => {
    if (activeTab === 'materials') {
      loadMaterialTypes(selectedCompanyID);
      loadMaterialShapes(selectedCompanyID);
    }
  }, [activeTab, selectedCompanyID, loadMaterialTypes, loadMaterialShapes]);

  useEffect(() => {
    ++manualMachinesRequest.current;
    ++manualMachineMutationRequest.current;
    setManualMachines([]);
    setManualMachinesError('');
    setManualMachinesLoading(false);
    setManualMachineForm(initialManualMachineForm);
    setEditingManualMachine(null);
    setShowManualMachineModal(false);
    setManualMachineMutationPending(false);
    if (activeTab === 'devices' && selectedCompanyID) loadManualMachines(selectedCompanyID);
  }, [activeTab, selectedCompanyID, loadManualMachines]);

  const handleCompanySubmit = async (event) => {
    event.preventDefault();
    try {
      await createAdminCompany(companyForm);
      setCompanyForm(initialCompanyForm);
      setSelectedCompanyID(companyForm.companyID);
      setShowCompanyModal(false);
      await loadCompanies();
      showSuccess(`[${companyForm.companyName}] 업체 및 관리자 계정을 성공적으로 등록했습니다.`);
    } catch (err) {
      showError(err);
    }
  };

  const handleUserSubmit = async (event) => {
    event.preventDefault();
    if (!selectedCompanyID) {
      showError(new Error('업체를 먼저 선택해 주세요.'));
      return;
    }
    try {
      await createAdminCompanyUser(selectedCompanyID, userForm);
      setUserForm(initialUserForm);
      setShowUserModal(false);
      await loadCompanyDetails(selectedCompanyID);
      showSuccess(`[${userForm.name}] 계정을 추가 등록했습니다.`);
    } catch (err) {
      showError(err);
    }
  };

  const openUserEdit = (user) => {
    setEditingUser({
      accountID: user.accountID,
      userID: user.userID || '',
      name: user.name || '',
      contactPhone: user.contactPhone || '',
      contactEmail: user.contactEmail || '',
      position: user.position || '',
      role: user.role || 'operator',
      isFirstLogin: Boolean(user.isFirstLogin),
    });
  };

  const handleUserUpdate = async (event) => {
    event.preventDefault();
    if (!editingUser || userMutationPending) return;
    const companyID = selectedCompanyID;
    setUserMutationPending(true);
    try {
      const { accountID, ...payload } = editingUser;
      await updateAdminCompanyUser(companyID, accountID, payload);
      if (selectedCompanyIDRef.current !== companyID) return;
      setEditingUser(null);
      await loadCompanyDetails(companyID);
      if (selectedCompanyIDRef.current === companyID) showSuccess('계정 정보를 수정했습니다.');
    } catch (err) {
      if (selectedCompanyIDRef.current === companyID) showError(err);
    } finally {
      setUserMutationPending(false);
    }
  };

  const handleUserPasswordReset = async (user) => {
    if (userMutationPending) return;
    if (!window.confirm('이 계정의 임시 비밀번호를 1234로 초기화하고 다음 로그인 시 변경을 요구합니다.')) return;
    const companyID = selectedCompanyID;
    setUserMutationPending(true);
    try {
      await resetAdminCompanyUserPassword(companyID, user.accountID);
      if (selectedCompanyIDRef.current !== companyID) return;
      await loadCompanyDetails(companyID);
      if (selectedCompanyIDRef.current === companyID) showSuccess('비밀번호가 초기화되었습니다. 임시 비밀번호: 1234');
    } catch (err) {
      if (selectedCompanyIDRef.current === companyID) showError(err);
    } finally {
      setUserMutationPending(false);
    }
  };

  const handleMaterialTypeCreate = async (event) => {
    event.preventDefault();
    if (!selectedCompanyID) return;
    const companyID = selectedCompanyID;
    try {
      await createAdminCompanyMaterialType(companyID, {
        code: Number(materialTypeForm.code),
        label: materialTypeForm.label.trim(),
        active: true,
        displayOrder: Number(materialTypeForm.displayOrder),
      });
      if (selectedCompanyIDRef.current !== companyID) return;
      setMaterialTypeForm(initialMaterialTypeForm);
      await loadMaterialTypes(companyID);
      if (selectedCompanyIDRef.current !== companyID) return;
      showSuccess('소재 종류를 추가했습니다.');
    } catch (err) {
      if (selectedCompanyIDRef.current === companyID) showError(err);
    }
  };

  const updateMaterialDraft = (code, changes) => {
    setMaterialTypes((current) => current.map((material) => (
      material.code === code ? { ...material, ...changes } : material
    )));
  };

  const persistMaterialType = async (material, changes) => {
    if (!selectedCompanyID) return;
    const companyID = selectedCompanyID;
    try {
      if (material.builtIn && (material.persisted === false || !material.createdAt)) {
        await createAdminCompanyMaterialType(companyID, {
          code: material.code,
          label: changes.label ?? material.label,
          active: changes.active ?? material.active,
          displayOrder: changes.displayOrder ?? Number(material.displayOrder),
        });
      } else {
        await updateAdminCompanyMaterialType(companyID, material.code, changes);
      }
      if (selectedCompanyIDRef.current !== companyID) return;
      await loadMaterialTypes(companyID);
      if (selectedCompanyIDRef.current !== companyID) return;
      showSuccess(`소재 코드 ${material.code}을(를) 저장했습니다.`);
    } catch (err) {
      if (selectedCompanyIDRef.current === companyID) showError(err);
    }
  };

  const handleMaterialShapeCreate = async (event) => {
    event.preventDefault();
    if (!selectedCompanyID) return;
    const companyID = selectedCompanyID;
    try {
      await createAdminCompanyMaterialShape(companyID, {
        code: Number(materialShapeForm.code),
        label: materialShapeForm.label.trim(),
        active: true,
        displayOrder: Number(materialShapeForm.displayOrder),
      });
      if (selectedCompanyIDRef.current !== companyID) return;
      setMaterialShapeForm(initialMaterialShapeForm);
      await loadMaterialShapes(companyID);
      if (selectedCompanyIDRef.current !== companyID) return;
      showSuccess('소재 모양을 추가했습니다.');
    } catch (err) {
      if (selectedCompanyIDRef.current === companyID) showError(err);
    }
  };

  const updateMaterialShapeDraft = (code, changes) => {
    setMaterialShapes((current) => current.map((shape) => (
      shape.code === code ? { ...shape, ...changes } : shape
    )));
  };

  const persistMaterialShape = async (shape, changes) => {
    if (!selectedCompanyID) return;
    const companyID = selectedCompanyID;
    try {
      if (shape.builtIn && shape.persisted === false) {
        await createAdminCompanyMaterialShape(companyID, {
          code: shape.code,
          label: changes.label ?? shape.label,
          active: changes.active ?? shape.active,
          displayOrder: changes.displayOrder ?? Number(shape.displayOrder),
        });
      } else {
        await updateAdminCompanyMaterialShape(companyID, shape.code, changes);
      }
      if (selectedCompanyIDRef.current !== companyID) return;
      await loadMaterialShapes(companyID);
      if (selectedCompanyIDRef.current !== companyID) return;
      showSuccess(`소재 모양 코드 ${shape.code}을(를) 저장했습니다.`);
    } catch (err) {
      if (selectedCompanyIDRef.current === companyID) showError(err);
    }
  };

  const openManualMachineCreate = () => {
    setEditingManualMachine(null);
    setManualMachineForm(initialManualMachineForm);
    setShowManualMachineModal(true);
  };

  const openManualMachineEdit = (machine) => {
    setEditingManualMachine(machine);
    setManualMachineForm({
      machineName: machine.machineName || '',
      description: machine.description || '',
    });
    setShowManualMachineModal(true);
  };

  const handleManualMachineSubmit = async (event) => {
    event.preventDefault();
    if (manualMachineMutationPending || !selectedCompanyID) return;
    const machineName = manualMachineForm.machineName.trim();
    const description = manualMachineForm.description.trim();
    if (!machineName) {
      showError(new Error('기타 장비명은 필수입니다.'));
      return;
    }
    const companyID = selectedCompanyID;
    const editing = editingManualMachine;
    const mutationID = ++manualMachineMutationRequest.current;
    setManualMachineMutationPending(true);
    try {
      if (editing) {
        await updateAdminManualMachine(companyID, editing.id, {
          machineName,
          description,
        });
      } else {
        await createAdminManualMachine(companyID, { machineName, description });
      }
      if (mutationID !== manualMachineMutationRequest.current || selectedCompanyIDRef.current !== companyID) return;
      const reloaded = await loadManualMachines(companyID);
      if (!reloaded || mutationID !== manualMachineMutationRequest.current || selectedCompanyIDRef.current !== companyID) return;
      setShowManualMachineModal(false);
      setEditingManualMachine(null);
      setManualMachineForm(initialManualMachineForm);
      showSuccess(editing ? '기타 장비를 수정했습니다.' : '기타 장비를 추가했습니다.');
    } catch (err) {
      if (mutationID === manualMachineMutationRequest.current && selectedCompanyIDRef.current === companyID) showError(err);
    } finally {
      if (mutationID === manualMachineMutationRequest.current && selectedCompanyIDRef.current === companyID) {
        setManualMachineMutationPending(false);
      }
    }
  };

  const handleManualMachineDelete = async (machine) => {
    if (manualMachineMutationPending || !selectedCompanyID) return;
    const warning = `${machine.machineName} 기타 장비를 영구 삭제할까요? 장비 마스터는 영구 삭제되지만 과거 작업지시의 장비명 스냅샷은 유지됩니다.`;
    if (!window.confirm(warning)) return;
    const companyID = selectedCompanyID;
    const mutationID = ++manualMachineMutationRequest.current;
    setManualMachineMutationPending(true);
    try {
      await deleteAdminManualMachine(companyID, machine.id);
      if (mutationID !== manualMachineMutationRequest.current || selectedCompanyIDRef.current !== companyID) return;
      const reloaded = await loadManualMachines(companyID);
      if (!reloaded || mutationID !== manualMachineMutationRequest.current || selectedCompanyIDRef.current !== companyID) return;
      showSuccess('기타 장비를 삭제했습니다.');
    } catch (err) {
      if (mutationID === manualMachineMutationRequest.current && selectedCompanyIDRef.current === companyID) showError(err);
    } finally {
      if (mutationID === manualMachineMutationRequest.current && selectedCompanyIDRef.current === companyID) {
        setManualMachineMutationPending(false);
      }
    }
  };

  const openPassivePLCCreate = () => {
    setEditingPassivePLC(null);
    setPassivePLCForm({ ...initialPassivePLCForm, gatewayID: gatewayID.trim() });
    setShowPassivePLCModal(true);
  };

  const openPassivePLCEdit = (machine) => {
    const completionConfig = createPassiveCompletionConfig(machine.passiveCompletionConfig);
    setEditingPassivePLC(machine);
    setPassivePLCForm({
      combinedID: String(machine.id ?? machine.combinedID ?? '').padStart(8, '0'),
      machineName: machine.machineName || '',
      gatewayID: machine.gatewayID || '',
      passiveProtocol: machine.passiveProtocol || 'omron_tcp',
      enabled: machine.enabled !== false,
      completionMode: completionModeFromConfig(completionConfig),
      divisors: completionConfig.divisors,
    });
    setShowPassivePLCModal(true);
  };

  const handlePassivePLCSubmit = async (event) => {
    event.preventDefault();
    if (passiveMutationPending || !selectedCompanyID) return;
    const payload = {
      machineName: passivePLCForm.machineName.trim(),
      gatewayID: passivePLCForm.gatewayID.trim(),
      passiveProtocol: passivePLCForm.passiveProtocol,
      enabled: passivePLCForm.enabled,
      passiveCompletionConfig: buildPassiveCompletionConfig({
        mode: passivePLCForm.completionMode,
        divisors: passivePLCForm.divisors,
      }),
    };
    if (!/^[0-9]{8}$/.test(passivePLCForm.combinedID)
      || passivePLCForm.combinedID.slice(1, 4) !== selectedCompanyID
      || !payload.machineName || !payload.gatewayID) {
      showError(new Error('Combined ID, 장비명, Gateway ID를 확인해 주세요.'));
      return;
    }
    const companyID = selectedCompanyID;
    setPassiveMutationPending(true);
    try {
      if (editingPassivePLC) {
        await updateAdminCompanyMachine(companyID, passivePLCForm.combinedID, payload);
      } else {
        await createAdminCompanyMachine(companyID, { combinedID: passivePLCForm.combinedID, ...payload });
      }
      if (selectedCompanyIDRef.current !== companyID) return;
      await loadCompanyDetails(companyID);
      if (selectedCompanyIDRef.current !== companyID) return;
      setShowPassivePLCModal(false);
      setEditingPassivePLC(null);
      setPassivePLCForm(initialPassivePLCForm);
    } catch (err) {
      if (selectedCompanyIDRef.current === companyID) showError(err);
    } finally {
      setPassiveMutationPending(false);
    }
  };

  const openGatewayPLCModal = (config = null, machine = null) => {
    if (config) {
      setEditingGatewayPLCID(config.id);
      setGatewayPLCForm({
        combinedID: String(config.machineID).padStart(8, '0'), machineName: config.machineName,
        companyID: config.companyID, machineID: String(config.machineID), enabled: config.enabled,
        protocol: config.protocol, mode: config.mode, host: config.host, port: String(config.port), unitID: String(config.unitID),
        profileName: config.profileName, byteOrder: config.byteOrder,
        asciiByteOrder: config.asciiByteOrder || 'BE', wordSwap: config.wordSwap,
        pollMs: String(config.pollMs), timeoutMs: String(config.timeoutMs),
        writeEnabled: config.writeEnabled ?? true,
        completionConfig: createDefaultGatewayPLCCompletionConfig(config.completionConfig),
        baseOffset: String(config.memoryMap?.baseOffset ?? 0),
        registers: createRegisterRows(config.memoryMap?.registers),
        configVersion: config.configVersion,
      });
    } else {
      setEditingGatewayPLCID('');
      setGatewayPLCForm({
        ...initialGatewayPLCForm,
        combinedID: machine ? String(machine.id ?? machine.combinedID ?? '').padStart(8, '0') : '',
        machineName: machine?.machineName || '',
        completionConfig: createDefaultGatewayPLCCompletionConfig(),
        registers: createRegisterRows(),
        companyID: selectedCompanyID,
        machineID: '',
      });
    }
    setShowGatewayPLCModal(true);
  };

  const handleGatewayPLCSubmit = async (event) => {
    event.preventDefault();
    if (gatewayMutationPending) return;
    const validationErrors = validateGatewayPLCConfig(gatewayPLCForm);
    if (!editingGatewayPLCID) {
      if (!/^[0-9]{8}$/.test(gatewayPLCForm.combinedID)) validationErrors.push('Combined ID 8자리');
      if (!gatewayPLCForm.machineName.trim()) validationErrors.push('설비 별칭');
      if (gatewayPLCForm.combinedID.slice(1, 4) !== selectedCompanyID) validationErrors.push('Combined ID 업체 불일치');
    }
    if (validationErrors.length > 0) {
      showError(new Error(`Modbus 설정 오류: ${validationErrors.join(', ')}`));
      return;
    }
    setGatewayMutationPending(true);
    try {
      const payload = buildGatewayPLCConfigPayload(gatewayPLCForm);
      if (editingGatewayPLCID) {
        const { companyID, machineID, ...mutablePayload } = payload;
        await updateAdminGatewayPLCConfig(gatewayID.trim(), editingGatewayPLCID, {
          ...mutablePayload,
          expectedVersion: gatewayPLCForm.configVersion,
        });
      } else {
        await createAdminGatewayPLCConfig(gatewayID.trim(), {
          ...payload,
          combinedID: gatewayPLCForm.combinedID,
          machineName: gatewayPLCForm.machineName.trim(),
          companyID: selectedCompanyID,
          machineID: 0,
          writeEnabled: false,
        });
      }
      setShowGatewayPLCModal(false);
      await Promise.all([loadGatewayPLCConfigs(), loadCompanyDetails(selectedCompanyID)]);
      showSuccess(`Gateway [${gatewayID}] PLC 접속 설정을 저장했습니다.`);
    } catch (err) {
      showError(err);
    } finally {
      setGatewayMutationPending(false);
    }
  };

  const handleGatewayPLCDelete = async (config) => {
    if (gatewayMutationPending || !window.confirm(`${config.machineName} PLC 접속 설정을 삭제할까요?`)) return;
    setGatewayMutationPending(true);
    try {
      await deleteAdminGatewayPLCConfig(gatewayID.trim(), config.id, config.configVersion);
      await loadGatewayPLCConfigs();
      showSuccess(`${config.machineName} PLC 접속 설정을 삭제했습니다.`);
    } catch (err) {
      showError(err);
    } finally {
      setGatewayMutationPending(false);
    }
  };

  const handleGatewayPLCYAMLExport = async () => {
    const trimmedGatewayID = gatewayID.trim();
    if (!trimmedGatewayID) return;
    try {
      const yamlBlob = await exportAdminGatewayPLCConfigsYAML(trimmedGatewayID);
      const objectURL = URL.createObjectURL(yamlBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectURL;
      downloadLink.download = `${trimmedGatewayID}-modbus.yaml`;
      downloadLink.click();
      URL.revokeObjectURL(objectURL);
    } catch (err) {
      showError(err);
    }
  };

  const normalizeCombinedID = (value) => String(value ?? '').padStart(8, '0');
  const gatewayConfigByMachineID = new Map(
    gatewayPLCConfigs.map((config) => [normalizeCombinedID(config.machineID ?? config.combinedID), config]),
  );
  const machineIDs = new Set(machines.map((machine) => normalizeCombinedID(machine.id ?? machine.combinedID)));
  const integratedDevices = [
    ...machines.map((machine) => {
      const combinedID = normalizeCombinedID(machine.id ?? machine.combinedID);
      return { combinedID, machine, config: gatewayConfigByMachineID.get(combinedID) };
    }),
    ...gatewayPLCConfigs
      .filter((config) => !machineIDs.has(normalizeCombinedID(config.machineID ?? config.combinedID)))
      .map((config) => ({
        combinedID: normalizeCombinedID(config.machineID ?? config.combinedID),
        machine: null,
        config,
      })),
  ];

  return (
    <div className="admin-page-background">
      <div className="admin-main-container">
        <div className="admin-portal-shell">
          <aside className="admin-company-sidebar" aria-label="업체 선택">
            <div className="admin-portal-identity">
              <span className="admin-port-badge">ADMIN PORT</span>
              <h1>시스템 관리자 포털</h1>
              <p>업체·담당자 계정·Gateway 장비만 관리합니다.</p>
            </div>
            <div className="admin-sidebar-heading">
              <div>
                <strong>업체 목록</strong>
                <span>{companies.length}개 업체</span>
              </div>
              <button className="admin-icon-action" aria-label="빠른 업체 등록" onClick={() => setShowCompanyModal(true)}>＋</button>
            </div>
            <div className="admin-company-list">
              {companies.map((company) => (
                <button
                  key={company.id}
                  type="button"
                  className={`admin-company-list-item ${company.id === selectedCompanyID ? 'active' : ''}`}
                  onClick={() => setSelectedCompanyID(company.id)}
                >
                  <span className="admin-company-list-id">{company.id}</span>
                  <span>
                    <strong>{company.name}</strong>
                    <small>{company.isActive === false ? '비활성 업체' : '활성 업체'}</small>
                  </span>
                </button>
              ))}
              {companies.length === 0 && <p className="admin-sidebar-empty">등록된 업체가 없습니다.</p>}
            </div>
            <p className="admin-sidebar-scope">생산 대시보드와 작업지시는 업체 전용 포트에서 제공합니다.</p>
          </aside>

          <main className="admin-company-workspace">
            <div className="admin-title-bar">
              <div>
                <h2>{selectedCompany ? `${selectedCompany.name} 관리` : '업체를 선택해 주세요'}</h2>
                <span className="admin-subtitle">{loading ? '데이터 동기화 중...' : `업체 ID ${selectedCompanyID || '-'}`}</span>
              </div>
            </div>

            {message && <div className="admin-alert success-toast">{message}</div>}
            {error && <div className="admin-alert error-toast">{error}</div>}

            <div className="admin-scope-banner">
              서버 관리 범위: 업체 등록, 담당자 계정, 업체별 장비와 Gateway/PLC 설정
            </div>

            <section className="admin-summary-grid compact" aria-label="선택 업체 요약">
              <div className="admin-summary-card"><strong>{users.length}</strong><span>담당자·작업자 계정</span></div>
              <div className="admin-summary-card">
                <strong>{machines.length}</strong>
                <span>등록 장비 · Gateway PLC</span>
                <small>PLC 연결 {gatewayPLCConfigs.length}건</small>
              </div>
            </section>

            <div className="admin-tab-bar">
              <button className={`admin-tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>담당자 계정</button>
              <button className={`admin-tab-btn ${activeTab === 'devices' ? 'active' : ''}`} onClick={() => setActiveTab('devices')}>장비 · Gateway PLC</button>
              <button className={`admin-tab-btn ${activeTab === 'materials' ? 'active' : ''}`} onClick={() => setActiveTab('materials')}>소재 종류</button>
            </div>

            <div className="admin-content-pane">

          {/* TAB 2: 사용자 계정 관리 */}
          {activeTab === 'users' && (
            <div className="admin-tab-content fade-in">
              <div className="content-header-row">
                <div>
                  <h2>선택 업체 계정 정보 {selectedCompany ? `[ ${selectedCompany.name} ]` : ''}</h2>
                  <p className="subtext">업체 시스템에 로그인 가능한 사용자 및 작업자 계정 목록입니다.</p>
                </div>
                {selectedCompanyID && (
                  <button className="admin-action-btn" onClick={() => setShowUserModal(true)}>
                    ➕ 새 계정 추가
                  </button>
                )}
              </div>

              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>사용자 ID</th>
                      <th>이름</th>
                      <th>권한 등급</th>
                      <th>연락처</th>
                      <th>이메일 주소</th>
                      <th>직책</th>
                      <th>최초 로그인 여부</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.accountID || user.userID}>
                        <td><strong>{user.userID}</strong></td>
                        <td>{user.name}</td>
                        <td>
                          <span className={`role-badge ${user.role}`}>
                            {user.role === 'company_admin' ? '업체 관리자' : '일반 작업자'}
                          </span>
                        </td>
                        <td>{user.contactPhone || '-'}</td>
                        <td>{user.contactEmail || '-'}</td>
                        <td>{user.position || '-'}</td>
                        <td>
                          <span className={`status-badge ${user.isFirstLogin}`}>
                            {user.isFirstLogin ? '대기중(Y)' : '완료(N)'}
                          </span>
                        </td>
                        <td>
                          <button type="button" className="admin-action-btn secondary" disabled={userMutationPending} onClick={() => openUserEdit(user)}>수정</button>
                          <button type="button" className="admin-action-btn secondary" disabled={userMutationPending} onClick={() => handleUserPasswordReset(user)}>비밀번호 초기화</button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan="8" className="table-empty">업체를 선택하지 않았거나 등록된 사용자 계정이 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'devices' && (
            <div className="admin-tab-content fade-in">
              <div className="content-header-row">
                <div>
                  <h2>장비 · Gateway PLC 통합 관리 {selectedCompany ? `[ ${selectedCompany.name} ]` : ''}</h2>
                  <p className="subtext">Combined ID를 기준으로 설비 기본정보와 Gateway PLC 접속 설정을 한 목록에서 관리합니다.</p>
                </div>
                <div className="content-header-actions">
                  <button type="button" className="admin-action-btn secondary" disabled={!selectedCompanyID} onClick={openPassivePLCCreate}>
                    오므론·미쓰비시 PLC 등록
                  </button>
                  <button className="admin-action-btn" disabled={!selectedCompanyID || !gatewayID.trim()} onClick={() => openGatewayPLCModal()}>
                    ➕ Modbus TCP PLC 접속 등록
                  </button>
                </div>
              </div>
              <div className="form-grid gateway-filter-grid integrated-device-filter">
                <div className="form-group">
                  <label>Gateway ID</label>
                  <input value={gatewayID} onChange={(e) => setGatewayID(e.target.value)} placeholder="factory-a-gw-01" />
                </div>
                <div className="form-group gateway-refresh-group">
                  <button type="button" className="admin-action-btn secondary" onClick={loadGatewayPLCConfigs}>설정 새로고침</button>
                  <button type="button" className="admin-action-btn secondary" disabled={!gatewayID.trim()} onClick={handleGatewayPLCYAMLExport}>YAML 내보내기</button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="admin-table integrated-device-table">
                  <thead>
                    <tr>
                      <th>Combined ID / 장비</th>
                      <th>설비 기본정보</th>
                      <th>Gateway / PLC</th>
                      <th>통신 설정</th>
                      <th>상태 / 적용 버전</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integratedDevices.map(({ combinedID, machine, config }) => (
                      <tr key={config?.id || combinedID}>
                        <td>
                          <code className="combined-id-code">{combinedID || '-'}</code><br />
                          <strong>{machine?.machineName || config?.machineName || '이름 미등록'}</strong>
                        </td>
                        <td>
                          {machine ? (
                            <>터치 {machine.touchType} · 모델 {machine.modelID}<br />{machine.machineNo}호기</>
                          ) : <span className="device-missing-state">설비 마스터 미등록</span>}
                        </td>
                        <td>
                          {config ? (
                            <><code>{gatewayID}</code><br /><code>{config.host}</code></>
                          ) : machine?.connectionType === 'passive_tcp' ? (
                            <><code>{machine.gatewayID}</code><br /><span>PLC가 Gateway로 자동 접속</span></>
                          ) : <span className="device-missing-state">PLC 미연결</span>}
                        </td>
                        <td>
                          {config ? (
                            <>{config.port} / {config.unitID}<br />{config.profileName || '-'} {config.byteOrder || ''}{config.wordSwap ? ' + Word Swap' : ''}</>
                          ) : machine?.connectionType === 'passive_tcp' ? (
                            passiveProtocolLabels[machine.passiveProtocol] || machine.passiveProtocol
                          ) : '-'}
                        </td>
                        <td>
                          {config ? (
                            <>
                              <span className={`status-badge ${config.enabled}`}>{config.enabled ? '활성' : '비활성'}</span><br />
                              <GatewayApplyStatus config={config} />
                            </>
                          ) : machine?.connectionType === 'passive_tcp' ? (
                            <><span className={`status-badge ${machine.enabled !== false}`}>{machine.enabled !== false ? '활성' : '비활성'}</span><br /><small>연결 상태는 300B 수신 기준</small></>
                          ) : <span className="status-badge false">미연결</span>}
                        </td>
                        <td>
                          {config ? (
                            <>
                              <button className="admin-inline-btn" onClick={() => openGatewayPLCModal(config)}>수정</button>
                              <button className="admin-inline-btn danger" onClick={() => handleGatewayPLCDelete(config)}>삭제</button>
                            </>
                          ) : machine?.connectionType === 'passive_tcp' ? (
                            <button className="admin-inline-btn" onClick={() => openPassivePLCEdit(machine)}>등록정보 수정</button>
                          ) : (
                            <button className="admin-inline-btn" onClick={() => openGatewayPLCModal(null, machine)}>PLC 연결</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {integratedDevices.length === 0 && (
                      <tr><td colSpan="6" className="table-empty">선택 업체에 등록된 장비가 없습니다. 장비와 PLC 접속정보를 함께 등록해 주세요.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <section className="manual-machines-section" aria-label="기타 장비 관리">
                <div className="manual-machines-header">
                  <div>
                    <h3>기타 장비</h3>
                    <p>선택한 클라이언트의 PLC 비연결 장비 마스터를 별도로 관리합니다.</p>
                  </div>
                  <button type="button" className="admin-action-btn" disabled={!selectedCompanyID || manualMachineMutationPending} onClick={openManualMachineCreate}>
                    기타 장비 추가
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="admin-table manual-machines-table">
                    <thead><tr><th>장비명</th><th>설명</th><th>최종 수정시간</th><th>관리</th></tr></thead>
                    <tbody>
                      {manualMachines.map((machine) => (
                        <tr key={machine.id}>
                          <td><strong>{machine.machineName}</strong></td>
                          <td>{machine.description || '-'}</td>
                          <td>{formatManualMachineUpdatedAt(machine.updatedAt)}</td>
                          <td>
                            <button type="button" className="admin-inline-btn" aria-label="기타 장비 수정" disabled={manualMachineMutationPending} onClick={() => openManualMachineEdit(machine)}>수정</button>
                            <button type="button" className="admin-inline-btn danger" aria-label="기타 장비 삭제" disabled={manualMachineMutationPending} onClick={() => handleManualMachineDelete(machine)}>삭제</button>
                          </td>
                        </tr>
                      ))}
                      {manualMachinesLoading && <tr><td colSpan="4" className="table-empty">기타 장비를 불러오는 중입니다.</td></tr>}
                      {!manualMachinesLoading && manualMachinesError && <tr><td colSpan="4" className="table-empty error-state">{manualMachinesError}</td></tr>}
                      {!manualMachinesLoading && !manualMachinesError && manualMachines.length === 0 && <tr><td colSpan="4" className="table-empty">등록된 기타 장비가 없습니다.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'materials' && (
            <div className="admin-tab-content fade-in">
              <div className="content-header-row">
                <div>
                  <h2>소재 종류 관리 {selectedCompany ? `[ ${selectedCompany.name} ]` : ''}</h2>
                  <p className="subtext">기본 소재를 업체별로 재정의하거나 사용자 정의 소재를 추가하고 활성 상태를 관리합니다.</p>
                </div>
              </div>
              <form className="form-grid" onSubmit={handleMaterialTypeCreate}>
                <div className="form-group">
                  <label htmlFor="new-material-code">새 소재 코드</label>
                  <input id="new-material-code" type="number" min="1" value={materialTypeForm.code} onChange={(event) => setMaterialTypeForm({ ...materialTypeForm, code: event.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="new-material-label">새 소재 이름</label>
                  <input id="new-material-label" maxLength="100" value={materialTypeForm.label} onChange={(event) => setMaterialTypeForm({ ...materialTypeForm, label: event.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="new-material-order">새 소재 표시 순서</label>
                  <input id="new-material-order" type="number" min="0" max="1000000" value={materialTypeForm.displayOrder} onChange={(event) => setMaterialTypeForm({ ...materialTypeForm, displayOrder: event.target.value })} required />
                </div>
                <div className="form-group">
                  <button type="submit" className="admin-action-btn" disabled={!selectedCompanyID}>소재 추가</button>
                </div>
              </form>
              <div className="table-responsive">
                <table className="admin-table">
                  <thead><tr><th>코드</th><th>이름</th><th>활성</th><th>표시 순서</th><th>관리</th></tr></thead>
                  <tbody>
                    {materialTypes.map((material) => (
                      <tr key={material.code}>
                        <td><strong>{material.code}</strong></td>
                        <td>
                          <label className="sr-only" htmlFor={`material-label-${material.code}`}>코드 {material.code} 이름</label>
                          <input id={`material-label-${material.code}`} maxLength="100" value={material.label} onChange={(event) => updateMaterialDraft(material.code, { label: event.target.value })} />
                        </td>
                        <td>{material.active ? '활성' : '비활성'}</td>
                        <td>
                          <label className="sr-only" htmlFor={`material-order-${material.code}`}>코드 {material.code} 표시 순서</label>
                          <input id={`material-order-${material.code}`} type="number" min="0" max="1000000" value={material.displayOrder} onChange={(event) => updateMaterialDraft(material.code, { displayOrder: Number(event.target.value) })} />
                        </td>
                        <td>
                          <button type="button" className="admin-inline-btn" aria-label={`코드 ${material.code} 저장`} onClick={() => persistMaterialType(material, { label: material.label, displayOrder: Number(material.displayOrder) })}>저장</button>
                          <button type="button" className={`admin-inline-btn ${material.active ? 'danger' : ''}`} aria-label={`코드 ${material.code} ${material.active ? '비활성화' : '재활성화'}`} onClick={() => persistMaterialType(material, { active: !material.active })}>{material.active ? '비활성화' : '재활성화'}</button>
                        </td>
                      </tr>
                    ))}
                    {materialTypes.length === 0 && <tr><td colSpan="5" className="table-empty">등록된 소재 종류가 없습니다.</td></tr>}
                  </tbody>
                </table>
              </div>

              <div className="form-section-divider">소재 모양 관리</div>
              <p className="subtext">원형·육각·사각뿐 아니라 클라이언트에서 사용하는 여러 소재 모양을 추가하고 작업지시 선택 여부를 관리합니다.</p>
              <form className="form-grid" onSubmit={handleMaterialShapeCreate}>
                <div className="form-group">
                  <label htmlFor="new-material-shape-code">새 소재 모양 코드</label>
                  <input id="new-material-shape-code" type="number" min="1" max="4294967295" value={materialShapeForm.code} onChange={(event) => setMaterialShapeForm({ ...materialShapeForm, code: event.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="new-material-shape-label">새 소재 모양 이름</label>
                  <input id="new-material-shape-label" maxLength="100" value={materialShapeForm.label} onChange={(event) => setMaterialShapeForm({ ...materialShapeForm, label: event.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="new-material-shape-order">새 소재 모양 표시 순서</label>
                  <input id="new-material-shape-order" type="number" min="0" max="1000000" value={materialShapeForm.displayOrder} onChange={(event) => setMaterialShapeForm({ ...materialShapeForm, displayOrder: event.target.value })} required />
                </div>
                <div className="form-group">
                  <button type="submit" className="admin-action-btn" disabled={!selectedCompanyID}>소재 모양 추가</button>
                </div>
              </form>
              <div className="table-responsive">
                <table className="admin-table">
                  <thead><tr><th>코드</th><th>이름</th><th>활성</th><th>표시 순서</th><th>관리</th></tr></thead>
                  <tbody>
                    {materialShapes.map((shape) => (
                      <tr key={shape.code}>
                        <td><strong>{shape.code}</strong></td>
                        <td>
                          <label className="sr-only" htmlFor={`material-shape-label-${shape.code}`}>모양 코드 {shape.code} 이름</label>
                          <input id={`material-shape-label-${shape.code}`} maxLength="100" value={shape.label} onChange={(event) => updateMaterialShapeDraft(shape.code, { label: event.target.value })} />
                        </td>
                        <td>{shape.active ? '활성' : '비활성'}</td>
                        <td>
                          <label className="sr-only" htmlFor={`material-shape-order-${shape.code}`}>모양 코드 {shape.code} 표시 순서</label>
                          <input id={`material-shape-order-${shape.code}`} type="number" min="0" max="1000000" value={shape.displayOrder} onChange={(event) => updateMaterialShapeDraft(shape.code, { displayOrder: Number(event.target.value) })} />
                        </td>
                        <td>
                          <button type="button" className="admin-inline-btn" aria-label={`모양 코드 ${shape.code} 저장`} onClick={() => persistMaterialShape(shape, { label: shape.label, displayOrder: Number(shape.displayOrder) })}>저장</button>
                          <button type="button" className={`admin-inline-btn ${shape.active ? 'danger' : ''}`} aria-label={`모양 코드 ${shape.code} ${shape.active ? '비활성화' : '재활성화'}`} onClick={() => persistMaterialShape(shape, { active: !shape.active })}>{shape.active ? '비활성화' : '재활성화'}</button>
                        </td>
                      </tr>
                    ))}
                    {materialShapes.length === 0 && <tr><td colSpan="5" className="table-empty">등록된 소재 모양이 없습니다.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

            </div>
          </main>
        </div>
        {/* ================================================================= */}
        {showCompanyModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal-window">
              <div className="modal-header">
                <h3>🏢 신규 파트너 업체 등록</h3>
                <button className="modal-close-btn" onClick={() => setShowCompanyModal(false)}>×</button>
              </div>
              <form onSubmit={handleCompanySubmit} className="admin-modal-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>업체 고유 ID (3자리 숫자)</label>
                    <input 
                      value={companyForm.companyID} 
                      maxLength="3" 
                      placeholder="예: 103" 
                      onChange={(e) => setCompanyForm({ ...companyForm, companyID: e.target.value })} 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>업체명</label>
                    <input 
                      value={companyForm.companyName} 
                      placeholder="예: 주식회사 델타가공" 
                      onChange={(e) => setCompanyForm({ ...companyForm, companyName: e.target.value })} 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>라이선스 키 / 비고</label>
                    <input 
                      value={companyForm.licenseKey} 
                      placeholder="발급 라이선스 코드" 
                      onChange={(e) => setCompanyForm({ ...companyForm, licenseKey: e.target.value })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>대표 담당자 이름</label>
                    <input 
                      value={companyForm.contactName} 
                      placeholder="홍길동" 
                      onChange={(e) => setCompanyForm({ ...companyForm, contactName: e.target.value })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>대표 담당자 연락처</label>
                    <input 
                      value={companyForm.contactPhone} 
                      placeholder="010-0000-0000" 
                      onChange={(e) => setCompanyForm({ ...companyForm, contactPhone: e.target.value })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>대표 담당자 이메일</label>
                    <input 
                      type="email" 
                      value={companyForm.contactEmail} 
                      placeholder="manager@example.com" 
                      onChange={(e) => setCompanyForm({ ...companyForm, contactEmail: e.target.value })} 
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>업체 메모</label>
                    <input 
                      value={companyForm.contactMemo} 
                      placeholder="공장 위치, 주요 품목 등 메모" 
                      onChange={(e) => setCompanyForm({ ...companyForm, contactMemo: e.target.value })} 
                    />
                  </div>
                </div>

                <div className="form-section-divider">초기 어드민(회사 관리자) 계정 생성</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>로그인 ID</label>
                    <input 
                      value={companyForm.adminUserID} 
                      placeholder="admin103"
                      onChange={(e) => setCompanyForm({ ...companyForm, adminUserID: e.target.value })} 
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="company-admin-password">비밀번호</label>
                    <input
                      id="company-admin-password"
                      type="password"
                      value={companyForm.adminPassword}
                      placeholder="********"
                      onChange={(e) => setCompanyForm({ ...companyForm, adminPassword: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>이름</label>
                    <input 
                      value={companyForm.adminName} 
                      placeholder="김관리"
                      onChange={(e) => setCompanyForm({ ...companyForm, adminName: e.target.value })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>연락처</label>
                    <input 
                      value={companyForm.adminPhone} 
                      placeholder="010-1234-5678"
                      onChange={(e) => setCompanyForm({ ...companyForm, adminPhone: e.target.value })} 
                    />
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowCompanyModal(false)}>취소</button>
                  <button type="submit" className="submit-btn">업체 등록 완료</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* 모달 2: 신규 계정 추가 */}
        {/* ================================================================= */}
        {showUserModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal-window">
              <div className="modal-header">
                <h3>👤 신규 계정 등록 [ {selectedCompany?.name} ]</h3>
                <button className="modal-close-btn" onClick={() => setShowUserModal(false)}>×</button>
              </div>
              <form onSubmit={handleUserSubmit} className="admin-modal-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>사용자 ID</label>
                    <input 
                      value={userForm.userID} 
                      placeholder="ID 입력" 
                      onChange={(e) => setUserForm({ ...userForm, userID: e.target.value })} 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-user-password">비밀번호</label>
                    <input
                      id="new-user-password"
                      type="password"
                      value={userForm.password}
                      placeholder="비밀번호 설정"
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>이름</label>
                    <input 
                      value={userForm.name} 
                      placeholder="사용자 이름" 
                      onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>연락처</label>
                    <input 
                      value={userForm.contactPhone} 
                      placeholder="010-0000-0000" 
                      onChange={(e) => setUserForm({ ...userForm, contactPhone: e.target.value })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>이메일</label>
                    <input 
                      type="email" 
                      value={userForm.contactEmail} 
                      placeholder="email@example.com" 
                      onChange={(e) => setUserForm({ ...userForm, contactEmail: e.target.value })} 
                    />
                  </div>
                  <div className="form-group">
                    <label>직책</label>
                    <input 
                      value={userForm.position} 
                      placeholder="예: 과장, 작업자" 
                      onChange={(e) => setUserForm({ ...userForm, position: e.target.value })} 
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>권한 등급</label>
                    <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                      <option value="company_admin">업체 관리자 (Company Admin)</option>
                      <option value="operator">일반 작업자 (Operator)</option>
                    </select>
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowUserModal(false)}>취소</button>
                  <button type="submit" className="submit-btn">계정 추가 완료</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {editingUser && (
          <div className="admin-modal-overlay" role="presentation">
            <div className="admin-modal-window" role="dialog" aria-modal="true" aria-labelledby="edit-user-title">
              <div className="modal-header">
                <h3 id="edit-user-title">계정 수정</h3>
                <button type="button" className="modal-close-btn" aria-label="계정 수정 닫기" disabled={userMutationPending} onClick={() => setEditingUser(null)}>×</button>
              </div>
              <form onSubmit={handleUserUpdate} className="admin-modal-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="edit-user-id">사용자 ID</label>
                    <input id="edit-user-id" value={editingUser.userID} onChange={(e) => setEditingUser({ ...editingUser, userID: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-user-name">이름</label>
                    <input id="edit-user-name" value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-user-phone">연락처</label>
                    <input id="edit-user-phone" value={editingUser.contactPhone} onChange={(e) => setEditingUser({ ...editingUser, contactPhone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-user-email">이메일</label>
                    <input id="edit-user-email" type="email" value={editingUser.contactEmail} onChange={(e) => setEditingUser({ ...editingUser, contactEmail: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-user-position">직책</label>
                    <input id="edit-user-position" value={editingUser.position} onChange={(e) => setEditingUser({ ...editingUser, position: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-user-role">권한 등급</label>
                    <select id="edit-user-role" value={editingUser.role} onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}>
                      <option value="company_admin">업체 관리자 (Company Admin)</option>
                      <option value="operator">일반 작업자 (Operator)</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="edit-user-first-login">
                      <input id="edit-user-first-login" type="checkbox" checked={editingUser.isFirstLogin} onChange={(e) => setEditingUser({ ...editingUser, isFirstLogin: e.target.checked })} />
                      최초 로그인 시 비밀번호 변경 필요
                    </label>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" disabled={userMutationPending} onClick={() => setEditingUser(null)}>취소</button>
                  <button type="submit" className="submit-btn" disabled={userMutationPending}>{userMutationPending ? '저장 중...' : '저장'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showManualMachineModal && (
          <div className="admin-modal-overlay" role="presentation">
            <div className="admin-modal-window manual-machine-modal" role="dialog" aria-modal="true" aria-labelledby="manual-machine-dialog-title">
              <div className="modal-header">
                <h3 id="manual-machine-dialog-title">{editingManualMachine ? '기타 장비 수정' : '기타 장비 추가'}</h3>
                <button type="button" className="modal-close-btn" aria-label="기타 장비 폼 닫기" disabled={manualMachineMutationPending} onClick={() => setShowManualMachineModal(false)}>×</button>
              </div>
              <form onSubmit={handleManualMachineSubmit} className="admin-modal-form">
                <div className="form-group">
                  <label htmlFor="manual-machine-name">기타 장비명</label>
                  <input id="manual-machine-name" value={manualMachineForm.machineName} onChange={(event) => setManualMachineForm({ ...manualMachineForm, machineName: event.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="manual-machine-description">기타 장비 설명</label>
                  <textarea id="manual-machine-description" value={manualMachineForm.description} onChange={(event) => setManualMachineForm({ ...manualMachineForm, description: event.target.value })} />
                </div>

                <div className="modal-actions">
                  <button type="button" className="cancel-btn" disabled={manualMachineMutationPending} onClick={() => setShowManualMachineModal(false)}>취소</button>
                  <button type="submit" className="submit-btn" disabled={manualMachineMutationPending}>
                    {manualMachineMutationPending ? '저장 중...' : (editingManualMachine ? '기타 장비 수정' : '기타 장비 추가')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPassivePLCModal && (
          <div className="admin-modal-overlay" role="presentation">
            <div className="admin-modal-window passive-plc-modal" role="dialog" aria-modal="true" aria-labelledby="passive-plc-dialog-title">
              <div className="modal-header">
                <h3 id="passive-plc-dialog-title">{editingPassivePLC ? 'Passive PLC 등록정보 수정' : 'Passive PLC 등록'}</h3>
                <button type="button" className="modal-close-btn" aria-label="Passive PLC 폼 닫기" disabled={passiveMutationPending} onClick={() => setShowPassivePLCModal(false)}>×</button>
              </div>
              <form onSubmit={handlePassivePLCSubmit} className="admin-modal-form">
                <p className="passive-plc-notice">
                  오므론·미쓰비시 PLC가 Gateway에 자동 접속하므로 IP 주소, Unit ID, 레지스터 설정은 입력하지 않습니다.
                </p>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="passive-plc-combined-id">Combined ID</label>
                    <input
                      id="passive-plc-combined-id"
                      value={passivePLCForm.combinedID}
                      inputMode="numeric"
                      pattern="[0-9]{8}"
                      maxLength="8"
                      disabled={Boolean(editingPassivePLC)}
                      onChange={(event) => {
                        const combinedID = event.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                        setPassivePLCForm({
                          ...passivePLCForm,
                          combinedID,
                          machineName: passivePLCForm.machineName || machineNameFromCombinedID(combinedID),
                        });
                      }}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="passive-plc-machine-name">장비명</label>
                    <input id="passive-plc-machine-name" value={passivePLCForm.machineName} onChange={(event) => setPassivePLCForm({ ...passivePLCForm, machineName: event.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="passive-plc-gateway-id">Gateway ID</label>
                    <input id="passive-plc-gateway-id" value={passivePLCForm.gatewayID} onChange={(event) => setPassivePLCForm({ ...passivePLCForm, gatewayID: event.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="passive-plc-protocol">통신 방식</label>
                    <select id="passive-plc-protocol" value={passivePLCForm.passiveProtocol} onChange={(event) => setPassivePLCForm({ ...passivePLCForm, passiveProtocol: event.target.value })}>
                      <option value="omron_tcp">오므론 Passive TCP (8081)</option>
                      <option value="mitsubishi_tcp">미쓰비시 Passive TCP (8082)</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="passive-plc-completion-mode">완료 기록 방식</label>
                    <select id="passive-plc-completion-mode" value={passivePLCForm.completionMode} onChange={(event) => setPassivePLCForm({ ...passivePLCForm, completionMode: event.target.value })}>
                      <option value="message">완료 메시지 사용</option>
                      <option value="signal">송신 신호 사용</option>
                      <option value="off">사용 안 함</option>
                    </select>
                  </div>
                  {[
                    ['cuttingLength', '절단길이 배율'],
                    ['materialSize1', '크기1 배율'],
                    ['materialSize2', '크기2 배율'],
                    ['remainingMaterial', '잔재량 배율'],
                  ].map(([field, label]) => (
                    <div className="form-group" key={field}>
                      <label htmlFor={`passive-plc-divisor-${field}`}>{label}</label>
                      <select
                        id={`passive-plc-divisor-${field}`}
                        value={passivePLCForm.divisors[field]}
                        onChange={(event) => setPassivePLCForm({
                          ...passivePLCForm,
                          divisors: { ...passivePLCForm.divisors, [field]: event.target.value },
                        })}
                      >
                        {PASSIVE_COMPLETION_DIVISORS.map((divisor) => <option key={divisor} value={divisor}>{divisor}</option>)}
                      </select>
                    </div>
                  ))}
                  <div className="form-group checkbox-group full-width">
                    <label htmlFor="passive-plc-enabled">
                      <input id="passive-plc-enabled" type="checkbox" checked={passivePLCForm.enabled} onChange={(event) => setPassivePLCForm({ ...passivePLCForm, enabled: event.target.checked })} />
                      활성화
                    </label>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" disabled={passiveMutationPending} onClick={() => setShowPassivePLCModal(false)}>취소</button>
                  <button type="submit" className="submit-btn" disabled={passiveMutationPending}>
                    {passiveMutationPending ? '저장 중...' : (editingPassivePLC ? '수정 저장' : '등록')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showGatewayPLCModal && (
          <div className="admin-modal-overlay" role="presentation">
            <div className={`admin-modal-window gateway-plc-modal ${editingGatewayPLCID ? 'gateway-plc-edit-workspace' : ''}`} role="dialog" aria-modal="true" aria-labelledby="gateway-plc-dialog-title">
              <div className="modal-header">
                <h3 id="gateway-plc-dialog-title">{editingGatewayPLCID ? 'Modbus TCP PLC 접속 수정' : 'Gateway 장비 통합 등록'}</h3>
                <button type="button" className="modal-close-btn" aria-label="PLC 접속 설정 닫기" disabled={gatewayMutationPending} onClick={() => setShowGatewayPLCModal(false)}>×</button>
              </div>
              {editingGatewayPLCID && (
                <section className="gateway-plc-identity-summary" aria-label="수정 대상 PLC">
                  <div>
                    <span>Combined ID (수정 불가)</span>
                    <strong>{gatewayPLCForm.combinedID}</strong>
                  </div>
                  <div>
                    <span>설비 별칭</span>
                    <strong>{gatewayPLCForm.machineName || '-'}</strong>
                  </div>
                  <div>
                    <span>Gateway</span>
                    <strong>{gatewayID.trim()}</strong>
                  </div>
                  <div>
                    <span>설정 Version</span>
                    <strong>설정 Version {gatewayPLCForm.configVersion}</strong>
                  </div>
                </section>
              )}
              <form onSubmit={handleGatewayPLCSubmit} className={`admin-modal-form ${editingGatewayPLCID ? 'gateway-plc-editor-form' : ''}`}>
                <div className="gateway-plc-editor-body" role="region" aria-label={editingGatewayPLCID ? 'Modbus TCP PLC 설정 편집' : 'Modbus TCP PLC 설정 등록'}>
                  <section className="gateway-connection-panel" aria-labelledby="gateway-connection-panel-title">
                    <div className="gateway-panel-heading">
                      <h4 id="gateway-connection-panel-title">PLC 접속 설정</h4>
                      <p>현장 PLC 네트워크와 통신 조건</p>
                    </div>
                    <div className="form-grid">
                  {!editingGatewayPLCID && (
                    <>
                      <div className="form-group">
                        <label htmlFor="gateway-plc-combined-id">Combined ID</label>
                        <input id="gateway-plc-combined-id" value={gatewayPLCForm.combinedID} inputMode="numeric" pattern="[0-9]{8}" maxLength="8" placeholder="10010001" onChange={(e) => {
                          const combinedID = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                          setGatewayPLCForm({ ...gatewayPLCForm, combinedID, machineName: machineNameFromCombinedID(combinedID) });
                        }} required />
                      </div>
                      <div className="form-group">
                        <label htmlFor="gateway-plc-machine-name">자동 장비명</label>
                        <input id="gateway-plc-machine-name" value={gatewayPLCForm.machineName} placeholder="Combined ID 입력 시 자동 설정" disabled required />
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label htmlFor="gateway-plc-host">PLC 고정 IP</label>
                    <input id="gateway-plc-host" value={gatewayPLCForm.host} placeholder="192.168.10.20" pattern="^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$" onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, host: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="gateway-plc-port">TCP Port</label>
                    <input id="gateway-plc-port" type="number" min="1" max="65535" value={gatewayPLCForm.port} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, port: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="gateway-plc-unit">Modbus Unit ID</label>
                    <input id="gateway-plc-unit" type="number" min="1" max="247" value={gatewayPLCForm.unitID} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, unitID: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="gateway-plc-profile">Register Profile</label>
                    <input id="gateway-plc-profile" value={gatewayPLCForm.profileName} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, profileName: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="gateway-plc-byte-order">Byte Order</label>
                    <select id="gateway-plc-byte-order" value={gatewayPLCForm.byteOrder} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, byteOrder: e.target.value })}>
                      <option value="BE">Big Endian (BE)</option><option value="LE">Little Endian (LE)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="gateway-plc-ascii-byte-order">ASCII Byte Order</label>
                    <select id="gateway-plc-ascii-byte-order" value={gatewayPLCForm.asciiByteOrder} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, asciiByteOrder: e.target.value })}>
                      <option value="BE">Big Endian (BE)</option><option value="LE">Little Endian (LE)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="gateway-plc-poll">Polling 주기 (ms)</label>
                    <input id="gateway-plc-poll" type="number" min="100" max="60000" value={gatewayPLCForm.pollMs} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, pollMs: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="gateway-plc-timeout">Timeout (ms)</label>
                    <input id="gateway-plc-timeout" type="number" min="100" max="60000" value={gatewayPLCForm.timeoutMs} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, timeoutMs: e.target.value })} required />
                  </div>
                  <div className="form-group checkbox-group">
                    <label><input type="checkbox" checked={gatewayPLCForm.wordSwap} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, wordSwap: e.target.checked })} /> Word Swap</label>
                    <label><input type="checkbox" checked={gatewayPLCForm.writeEnabled} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, writeEnabled: e.target.checked })} /> 쓰기 허용</label>
                    <label><input type="checkbox" checked={gatewayPLCForm.enabled} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, enabled: e.target.checked })} /> 활성화</label>
                  </div>
                    </div>
                  </section>
                <div className="gateway-plc-register-workspace" role="region" aria-label="Modbus 레지스터 설정">
                <section className="modbus-memory-map completion-memory-map" aria-labelledby="completion-memory-map-title">
                  <div className="memory-map-heading">
                    <div>
                      <h4 id="completion-memory-map-title">ModbusTCP 완료 기록 설정</h4>
                      <p>Passive TCP(Omron/Mitsubishi) frame offset과 구분되는 Modbus D 레지스터 설정입니다.</p>
                    </div>
                  </div>
                  <div className="checkbox-group">
                    <label><input type="checkbox" checked={gatewayPLCForm.completionConfig.messageEnabled} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, completionConfig: { ...gatewayPLCForm.completionConfig, messageEnabled: e.target.checked, ...(e.target.checked ? { signalEnabled: false } : {}) } })} /> 메시지 기반 완료 기록</label>
                    <label><input type="checkbox" checked={gatewayPLCForm.completionConfig.signalEnabled} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, completionConfig: { ...gatewayPLCForm.completionConfig, signalEnabled: e.target.checked, ...(e.target.checked ? { messageEnabled: false } : {}) } })} /> Signal 기반 완료 기록</label>
                  </div>
                  <p><strong>알람은 설정과 관계없이 항상 기록</strong>합니다.</p>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="completion-signal-address">D4042 Signal 주소 설정</label>
                      <input id="completion-signal-address" type="number" min="0" max="65534" value={gatewayPLCForm.completionConfig.signal.address} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, completionConfig: { ...gatewayPLCForm.completionConfig, signal: { ...gatewayPLCForm.completionConfig.signal, address: e.target.value } } })} required />
                    </div>
                    <div><strong>DWORD (u32, 2 WORD)</strong><br />활성값 1 · Gateway 처리 후 0 reset</div>
                  </div>
                  <p>완료 Payload D4044 ~ D4065: 숫자 필드는 Byte Order/Word Swap, 업체명은 ASCII Byte Order와 작업명 padding 규칙을 사용합니다.</p>
                  <div className="table-responsive">
                    <table className="memory-map-table">
                      <thead><tr><th>완료 데이터</th><th>주소</th><th>타입 / Words</th><th>Divisor</th></tr></thead>
                      <tbody>
                        {gatewayPLCForm.completionConfig.payload.map((field, index) => (
                          <tr key={field.name}>
                            <td><code>{field.name}</code>{field.name === 'company_name' && <div>업체명 ASCII 12 bytes</div>}</td>
                            <td><input aria-label={`${field.name} 완료 주소 설정`} type="number" min="0" max="65535" value={field.address} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, completionConfig: { ...gatewayPLCForm.completionConfig, payload: gatewayPLCForm.completionConfig.payload.map((row, rowIndex) => rowIndex === index ? { ...row, address: e.target.value } : row) } })} required /></td>
                            <td>{field.type === 'u32' ? 'u32' : 'ASCII'} / {field.words}</td>
                            <td>{field.type === 'u32' ? <select aria-label={`${field.name} 완료 배율`} value={field.divisor} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, completionConfig: { ...gatewayPLCForm.completionConfig, payload: gatewayPLCForm.completionConfig.payload.map((row, rowIndex) => rowIndex === index ? { ...row, divisor: e.target.value } : row) } })}><option value="1">1</option><option value="10">10</option><option value="100">100</option><option value="1000">1000</option></select> : '1'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
                <section className="modbus-memory-map" aria-labelledby="modbus-memory-map-title">
                  <div className="memory-map-heading">
                    <div>
                      <h4 id="modbus-memory-map-title">PLC Read / 작업지시 Write Modbus Memory Map</h4>
                      <p>이름, 타입, words, access는 고정이며 레지스터 주소만 변경할 수 있습니다.</p>
                    </div>
                    <div className="form-group base-offset-group">
                      <label htmlFor="gateway-plc-base-offset">Base Offset</label>
                      <input id="gateway-plc-base-offset" type="number" step="1" min="0" max="65535" value={gatewayPLCForm.baseOffset} onChange={(e) => setGatewayPLCForm({ ...gatewayPLCForm, baseOffset: e.target.value })} required />
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="memory-map-table">
                      <thead><tr><th>이름</th><th>주소</th><th>타입 / Words / Access</th><th>Divisor</th><th>실제 시작 주소</th></tr></thead>
                      {['read', 'write'].map((access) => (
                        <tbody key={access} aria-label={`${access} registers`}>
                          <tr className="memory-map-access-heading"><th colSpan="5" scope="rowgroup">{access === 'read' ? 'PLC → Gateway (Read)' : 'Gateway → PLC (Write)'}</th></tr>
                          {gatewayPLCForm.registers.map((register, index) => register.access === access && (
                          <tr key={`${register.access}-${register.name}`}>
                            <td><code>{register.name}</code></td>
                            <td>
                              <label className="sr-only" htmlFor={`gateway-plc-register-${register.access}-${register.name}`}>{register.name} {register.access} 주소</label>
                              <input
                                id={`gateway-plc-register-${register.access}-${register.name}`}
                                aria-label={`${register.name} ${register.access} 주소`}
                                type="number"
                                step="1"
                                min="0"
                                max="65535"
                                value={register.address}
                                onChange={(e) => setGatewayPLCForm({
                                  ...gatewayPLCForm,
                                  registers: gatewayPLCForm.registers.map((row, rowIndex) => (
                                    rowIndex === index ? { ...row, address: e.target.value } : row
                                  )),
                                })}
                                required
                              />
                            </td>
                            <td>{register.type} / {register.words} / {register.access}</td>
                            <td>
                              {register.access === 'read' && SCALABLE_READ_REGISTERS.has(register.name) ? (
                                <select
                                  aria-label={`${register.name} divisor`}
                                  value={register.divisor}
                                  onChange={(e) => setGatewayPLCForm({
                                    ...gatewayPLCForm,
                                    registers: gatewayPLCForm.registers.map((row, rowIndex) => (
                                      rowIndex === index ? { ...row, divisor: e.target.value } : row
                                    )),
                                  })}
                                >
                                  <option value="1">1</option>
                                  <option value="10">10</option>
                                  <option value="100">100</option>
                                  <option value="1000">1000</option>
                                </select>
                              ) : '1'}
                            </td>
                            <td>{Number(gatewayPLCForm.baseOffset || 0) + Number(register.address || 0)}</td>
                          </tr>
                          ))}
                        </tbody>
                      ))}
                    </table>
                  </div>
                </section>
                </div>
                </div>
                <div className="form-section-divider">Protocol: Modbus TCP · Mode: Gateway Active Poll</div>
                <div className={`modal-actions ${editingGatewayPLCID ? 'gateway-plc-sticky-actions' : ''}`}>
                  <button type="button" className="cancel-btn" disabled={gatewayMutationPending} onClick={() => setShowGatewayPLCModal(false)}>취소</button>
                  <button type="submit" className="submit-btn" disabled={gatewayMutationPending}>{gatewayMutationPending ? '저장 중...' : (editingGatewayPLCID ? '설정 수정' : 'Gateway 장비 등록')}</button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default AdminPage;
